import { $gamTargetingAtom } from '@client/core/atoms/gamTargeting.js';
import { addLifecycleEvent } from '@client/core/atoms/metrics.js';
import { $prebidTargetingAtom } from '@client/core/atoms/prebidTargeting.js';
import { isFeatureEnabled } from '@client/core/atoms/unleashFeatures.js';
import { formatDctr } from '@client/core/utils/prebid/formatDctr.js';
import {
  debugLog,
  UNLEASH_FEATURE_NAME
} from '@schibsted-nmp/advertising-shared';

import * as namespace from './.namespace.js';
import { Api } from './Api/index.js';
import { ConfigId } from './ConfigId.js';

function formatKeywordsForMsft(
  targeting: Record<string, string | string[]>
): string {
  return Object.entries(targeting)
    .flatMap(([key, value]) => {
      const values = Array.isArray(value) ? value : [value];
      return values.map((v) => `${key}=${v}`);
    })
    .join(',');
}

export namespace Slots {
  const nameof = namespace.RelevantDigital.nameof.prefix({ Slots });

  /**
   * Starts the Relevant Digital Prebid auction for the given slots, if present.
   * Returns a promise that resolves with the slots to be refreshed, or `null`
   * in case of an error or unmet condition. Even so, the slots may still be
   * refreshed afterwards - they'll just be lacking header bidding targeting.
   */
  export async function load(
    slots: ReadonlyArray<googletag.Slot | null | undefined>,
    ...abortSignals: ReadonlyArray<AbortSignal>
  ): Promise<ReadonlyArray<googletag.Slot> | null> {
    if (isFeatureEnabled(UNLEASH_FEATURE_NAME.killswitchPrebid)) return null;
    if (typeof window === 'undefined') return null;

    const configId = ConfigId.get();
    if (!configId) return null;

    if (!(await ConfigId.exists(configId, ...abortSignals))) {
      debugLog(
        'Config ID not found in Relevant Digital Prebid script',
        configId
      );
      return null;
    }

    // Filter out invalid slots and get their IDs:
    const allowedDivIds = slots
      .filter(
        (slot): slot is NonNullable<typeof slot> =>
          slot !== null &&
          slot !== undefined &&
          typeof slot.getSlotElementId === 'function'
      )
      .map((slot) => slot.getSlotElementId());

    // Don't proceed if we have no valid slots:
    if (allowedDivIds.length === 0) {
      debugLog('No valid slots found for Relevant Digital Prebid');
      return null;
    }

    // At the moment this part of the code takes into account only mappings related to Tori
    // we can check at later stage as we implement other brands how to properly
    // separate mapping concerns if need be
    const gamTargeting = $gamTargetingAtom.get();

    const gamToXandrMapping: Partial<Record<string, string>> = {
      recom_cat_1: 'nmp-recommerce-category_level_1',
      recom_cat_2: 'nmp-recommerce-category_level_2',
      recom_cat_3: 'nmp-recommerce-category_level_3'
    };

    // Create a new object with keys replaced where applicable:
    const transformedTargeting = Object.fromEntries(
      gamTargeting.map(({ key, value }) => [
        gamToXandrMapping[key] ?? key,
        value
      ])
    );

    const prebidTargetingArray = $prebidTargetingAtom.get();

    const prebidTargetingObject = Object.fromEntries(
      prebidTargetingArray.map(({ key, value }) => [key, value])
    );

    const isMsftAdapterEnabled = isFeatureEnabled(
      UNLEASH_FEATURE_NAME.enableMsftAdapter
    );
    const useGlobalPrebidConfigs = isFeatureEnabled(
      UNLEASH_FEATURE_NAME.enableGlobalPrebidConfigs
    );

    // Targeting config depends on two independent flags:
    // - useMsftAdapter ON: ortb2.site.keywords (msft adapter), NOT appnexusAuctionKeywords
    // - useMsftAdapter OFF: appnexusAuctionKeywords (legacy AppNexus adapter)
    // - useGlobalPrebidConfigs ON: ortb2.site.ext.data (can coexist with either adapter)
    try {
      await Api(...abortSignals).addPrebidConfig({
        // Legacy AppNexus keywords - only when msft adapter is disabled
        ...(!isMsftAdapterEnabled && {
          appnexusAuctionKeywords: transformedTargeting
        }),

        ...((isMsftAdapterEnabled || useGlobalPrebidConfigs) && {
          ortb2: {
            site: {
              ...(isMsftAdapterEnabled && {
                keywords: formatKeywordsForMsft(transformedTargeting)
              }),

              ...(useGlobalPrebidConfigs && {
                ext: {
                  data: prebidTargetingObject
                }
              })
            }
          }
        })
      });
    } catch (error) {
      debugLog('Failed adding targeting for Relevant Digital Prebid', error);
    }

    addLifecycleEvent(
      `Loading Relevant Digital Prebid: ${allowedDivIds.join(', ')}`
    );

    debugLog('Loading Relevant Digital Prebid for GAM', allowedDivIds);

    const { promise, resolve, reject } =
      Promise.withResolvers<ReadonlyArray<googletag.Slot> | null>();

    for (const abortSignal of abortSignals) {
      abortSignal.addEventListener('abort', reject);
    }

    // Failsafe in case RY stalls somehow (this should never happen):
    const timeout = window.setTimeout(() => {
      reject(
        new LoadTimeoutError(
          'Did not call googletag.refresh() within time limit'
        )
      );
    }, 5000);

    try {
      await Api(...abortSignals).loadPrebid({
        configId,
        manageAdserver: false,
        collapseEmptyDivs: true,
        noGpt: true,
        collapseBeforeAdFetch: false,
        noSlotReload: false,
        allowedDivIds,

        googletagCalls: {
          refresh(slots) {
            /*
              By not forwarding this call to `googletag.pubads().refresh()`,
              we enable ourselves instead of Relevant Yield to handle the
              refresh when we please.
            */

            resolve(slots ?? null);
          }
        },

        onSlotAndUnit(parameters) {
          try {
            const globalPrebidConfigsEnabled = isFeatureEnabled(
              UNLEASH_FEATURE_NAME.enableGlobalPrebidConfigs
            );

            const pubmaticDctrEnabled = isFeatureEnabled(
              UNLEASH_FEATURE_NAME.enablePubmaticDctr
            );

            const { pbAdUnit } = parameters.unit;
            if (!pbAdUnit) return;

            // Mirror contextual key-values to ortb2Imp.ext.data for all bidders (backward compat)
            if (
              globalPrebidConfigsEnabled &&
              Object.keys(prebidTargetingObject).length
            ) {
              pbAdUnit.ortb2Imp ??= {};
              pbAdUnit.ortb2Imp.ext ??= {};

              pbAdUnit.ortb2Imp.ext.data = {
                ...pbAdUnit.ortb2Imp.ext.data,
                ...prebidTargetingObject
              };

              debugLog('Mirrored contextual key-values to ortb2Imp.ext.data', {
                targeting: prebidTargetingObject
              });
            }

            // Apply DCTR to PubMatic bidder params
            if (
              pubmaticDctrEnabled &&
              prebidTargetingArray.length &&
              pbAdUnit.bids?.length
            ) {
              const dctrString = formatDctr(prebidTargetingArray);

              if (dctrString) {
                for (const bid of pbAdUnit.bids) {
                  if (
                    bid.bidder === 'pubmatic' ||
                    bid.bidder === 'pubmatic_execute_media'
                  ) {
                    bid.params ??= {};
                    bid.params.dctr = dctrString;

                    debugLog('Applied DCTR to PubMatic bidder', {
                      adUnit: pbAdUnit.code,
                      dctr: dctrString,
                      targeting: prebidTargetingArray
                    });
                  }
                }
              }
            }
          } catch (error) {
            reject(error);
          }
        },

        onBeforeAdRequest(parameters) {
          try {
            const requiredEventType = 'auctionInit';
            const { pbjs } = parameters.auction;
            const events = pbjs.getEvents();

            if (!events.length) {
              throw new UnmetLoadConditionError(
                'No events found in Prebid.js event log'
              );
            }

            const hasAuctionInit = events.some(
              ({ eventType }) => eventType === requiredEventType
            );

            if (!hasAuctionInit) {
              throw new UnmetLoadConditionError(
                `Missing required Prebid.js event: '${requiredEventType}'`
              );
            }

            debugLog('Valid auction detected, continuing...');
          } catch (error) {
            reject(error);
          }
        }
      });
    } catch (error) {
      debugLog('Failed requesting ads!', error);
      reject(error);
    } finally {
      for (const abortSignal of abortSignals) {
        abortSignal.removeEventListener('abort', reject);
      }

      window.clearTimeout(timeout);
    }

    return await promise;
  }

  /**
   * Error thrown when a load operation times out, e.g. an auction is taking too
   * long to complete.
   */
  export class LoadTimeoutError extends Error {
    static {
      this.prototype.name = nameof({ LoadTimeoutError });
    }

    constructor(cause: string) {
      super(`Failed loading due to timeout: ${cause}`);
    }
  }

  /**
   * Error thrown when a load condition is not met, e.g. when certain expected
   * events are not found in the Prebid.js event log.
   */
  export class UnmetLoadConditionError extends Error {
    static {
      this.prototype.name = nameof({ UnmetLoadConditionError });
    }

    constructor(unmetCondition: string) {
      super(`Failed loading due to unmet condition: ${unmetCondition}`);
    }
  }
}
